<template>
  <Overlay
    :open="isBasketOverlayOpen"
    :show-title="false"
    layout="right"
    enter-from="right"
    :auto-height="!overlayRecommendations?.length"
    data-testid="cart-overlay"
    :aria-description="$t('pdp.glasses.cart.description')"
    @update:open="closeBasketOverlay"
  >
    <header class="flex items-start gap-4">
      <h2 class="flex items-start text-2xl font-medium">
        <LazyIconFielmannCheckCircle
          class="mr-2 mt-[0.3rem] w-5 shrink-0 text-[#22c55e]"
        />{{ $t('pdp.glasses.cart.title') }}
      </h2>
      <FimButton
        no-padding
        :aria-label="$t('modal.close')"
        data-testid="cart-overlay-close"
        class="close-ic-color ml-auto mt-1"
        type="link-alternative"
        @click="closeBasketOverlay"
      >
        <template #icon>
          <LazyIconFielmannClose class="size-6" />
        </template>
      </FimButton>
    </header>
    <div
      class="flex flex-wrap items-center justify-between gap-6 pt-8"
      :class="{
        'border-b border-gray-400 pb-10': overlayRecommendations?.length,
      }"
    >
      <FimButton
        class="grow"
        type="secondary"
        :to="localePath(routeList.basket)"
        data-testid="go-to-cart"
        @click.capture="closeBasketOverlay"
        >{{ $t('pdp.glasses.cart.gotoBasket') }}</FimButton
      >
      <FimButton
        class="grow"
        type="primary"
        :to="localePath(routeList.checkout)"
        data-testid="go-to-checkout"
        @click.capture="
          () => {
            closeBasketOverlay()
            trackBeginCheckout(basketItems)
          }
        "
        >{{ $t('pdp.glasses.cart.gotoCheckout') }}</FimButton
      >
    </div>
    <FimRecommendations
      v-if="overlayRecommendations?.length"
      :recommendation-product="recommendationProduct"
      :product-recommendations="overlayRecommendations.slice(0, 4)"
      :title="$t('pdp.cart_overlay_recommendation_headline')"
      :current-product-category="currentProductCategory"
      layout="grid"
      location="Pop-Up"
      @click:recommendation="closeBasketOverlay"
    >
      <slot />
    </FimRecommendations>
  </Overlay>
</template>

<script setup lang="ts">
import FimRecommendations from '../FimRecommendations.vue'
import useUiState from '~/composables/ui/useUiState'
import {
  type PRODUCT_RECOMMENDATION_ATTRIBUTE_REF_RELATED,
  type PRODUCT_RECOMMENDATION_ATTRIBUTE_REF_XSELL,
  PRODUCT_RECOMMENDATION_ATTRIBUTE_REF_UPSELL,
  type ProductRecommendationAttribute,
} from '~/constants/product'
import type { ProductRecommendation } from '~/composables/useProductRecommendations'
import type { Product } from '@scayle/storefront-core'

const { items: basketItems } = await useFimBasket()
const { trackBeginCheckout } = await useTrackingEvents()

const props = defineProps({
  recommendationProduct: {
    type: Object as PropType<Product>,
    required: true,
  },
  productRecommendations: {
    type: Object as PropType<{
      [key in ProductRecommendationAttribute]?: ProductRecommendation[]
    }>,
    default: undefined,
  },
  recommendationAttribute: {
    type: String as PropType<
      | typeof PRODUCT_RECOMMENDATION_ATTRIBUTE_REF_RELATED
      | typeof PRODUCT_RECOMMENDATION_ATTRIBUTE_REF_UPSELL
      | typeof PRODUCT_RECOMMENDATION_ATTRIBUTE_REF_XSELL
    >,
    default: PRODUCT_RECOMMENDATION_ATTRIBUTE_REF_UPSELL,
  },
  currentProductCategory: FimRecommendations.props.currentProductCategory,
})
const localePath = useFimLocalePath()
const { isBasketOverlayOpen, closeBasketOverlay } = useUiState()

const overlayRecommendations = computed(
  () => props.productRecommendations?.[props.recommendationAttribute],
)

onUnmounted(() => closeBasketOverlay())
</script>
